import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  ancestorChain,
  fetchOrgNodeTypes,
  fetchOrgTree,
  type OrgNode,
  type OrgNodeType,
} from '@/features/org/orgApi'

export interface OrgUnitsApi {
  units: readonly OrgNode[]
  types: readonly OrgNodeType[]
  loading: boolean
  /**
   * Ro'yxat ISHONCHLImi.
   *
   * ⚠️ `false` da BO'SH ro'yxat "birlik yo'q" DEGANI EMAS — "javob yo'q"
   * degani. Ikkalasini bir xil ko'rsatish operatorni "bizda tuzilma
   * ochilmagan ekan" degan noto'g'ri xulosaga olib borardi.
   */
  reliable: boolean
  reason: 'ok' | 'no-permission' | 'failed'
  /** Chegaraga urilgan daraxt — ekranda OCHIQ aytiladi */
  truncated: boolean
  /** `id → birlik`, nomni izlash uchun */
  byId: ReadonlyMap<string, OrgNode>
  /**
   * Birlikning TO'LIQ yo'li — «Bosh ofis / Texnik bo'lim».
   *
   * ⚠️ Topilmasa `null` va bu "birlik yo'q" degani EMAS: u doira
   * tashqarisida yoki ro'yxat chegarasidan tashqarida bo'lishi mumkin.
   * Chaqiruvchi `null` ni id ko'rsatib to'ldiradi, bo'sh qoldirmaydi —
   * bo'sh katak "bog'lanish yo'q" degan YOLG'ON berardi.
   */
  pathOf: (id: string | null) => string | null
}

/**
 * Hisobni biriktirish uchun tashkiliy tuzilma birliklari.
 *
 * <h2>⚠️ MANBA — `platform` (`features/org/orgApi`)</h2>
 * Tuzilma YAGONA egada va u `Tuzilma` menyusida yaratiladi (egasi
 * qarori, 2026-08-22). Bu yerda faqat O'QILADI: `identity` o'z
 * birligini yaratmaydi, aks holda ikkita daraxt paydo bo'lardi.
 *
 * Kesh kaliti ham `platform` niki (`queryKeys.org.*`) — Tuzilma
 * sahifasi bilan bitta yozuv bo'lishiladi va ikkinchi so'rov ketmaydi.
 *
 * ⚠️ `features/*` bir-birini import qilmaydi degan qoidaning (07-FRONTEND
 * §6) shu yerdagi istisnosi `features/hr/useEmployeeNodes` bilan bir xil
 * sababga ega: daraxt bitta egada va uni nusxalash ikkinchi haqiqat
 * manbai bo'lardi.
 *
 * <h2>⚠️ HUQUQ CHEGARASI OCHIQ AYTILADI</h2>
 * `GET /api/org-nodes` `org.node.read` talab qiladi. Faqat `user.read`
 * bor operator hisobning bog'lanishini KO'RADI (id javobning o'zida),
 * lekin birliklar ro'yxatini ololmaydi — so'rov shunda umuman
 * yuborilmaydi, 403 ni kutib keyin yashirish keraksiz shovqin bo'lardi.
 */
export function useOrgUnits(enabled = true): OrgUnitsApi {
  const { can } = usePermissions()
  const allowed = can('org.node.read')
  const active = enabled && allowed

  const tree = useQuery({
    queryKey: queryKeys.org.tree,
    queryFn: ({ signal }) => fetchOrgTree(signal),
    enabled: active,
    staleTime: 5 * 60_000,
  })

  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    enabled: active,
    staleTime: 5 * 60_000,
  })

  const units = tree.data?.nodes ?? []
  const failed = tree.isError || types.isError

  return {
    units,
    types: types.data ?? [],
    loading: active && (tree.isPending || types.isPending),
    reliable: allowed && !failed,
    reason: !allowed ? 'no-permission' : failed ? 'failed' : 'ok',
    truncated: tree.data?.truncated ?? false,
    byId: new Map(units.map((unit) => [unit.id, unit])),
    pathOf: (id) => {
      if (id === null) return null
      const chain = ancestorChain(units, id)
      return chain.length === 0 ? null : chain.map((unit) => unit.displayName).join(' / ')
    },
  }
}
